'use client';

import { useState } from 'react';
import { Loader2, Search, ChevronDown, ChevronUp, Video, Film } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MomentCard } from '@/components/moments/MomentCard';
import { useMomento } from '@/hooks/useMomento';
import { cn } from '@/lib/utils';

interface SearchResult {
  postId: string;
  title: string;
  description?: string;
  muxPlaybackId: string;
  playbackUrl: string;
  isFullVideo: boolean;
  startTime?: number;
  endTime?: number;
  duration?: number;
  postTitle?: string;
  momentType?: string;
  similarity: number;
  topMoments?: Array<{
    startTime: number;
    endTime: number;
    description: string;
    similarity: number;
    momentType?: string;
  }>;
}

interface SearchMomentsResult {
  results: SearchResult[];
  count: number;
  groupedByPost: boolean;
  maxSimilarity: number;
  query: string;
}

const INITIAL_DISPLAY_COUNT = 5;

export function SearchMomentsToolUI({ part, status }: any) {
  const { setSelectedMoment } = useMomento();
  const [showAll, setShowAll] = useState(false);
  const result = part.result;
  const query = part.args?.query || '';

  if (status === 'loading' || !result) {
    return (
      <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
              Searching videos...
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
              {query ? `"${query}"` : 'Processing your request'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!result.results || result.results.length === 0) {
    return (
      <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-800 flex items-center justify-center">
            <Search className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
              No results found
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
              Try a different search query
            </p>
          </div>
        </div>
      </div>
    );
  }

  const displayResults = showAll
    ? result.results
    : result.results.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMore = result.results.length > INITIAL_DISPLAY_COUNT;
  const isGroupedByPost = result.groupedByPost;

  return (
    <div className="rounded-lg bg-secondary/30 border border-border overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary/50">
        <div className="flex items-center gap-2">
          {isGroupedByPost ? (
            <Video className="w-4 h-4 text-muted-foreground" />
          ) : (
            <Film className="w-4 h-4 text-muted-foreground" />
          )}
          <span className="text-sm font-medium">
            {result.count} {isGroupedByPost ? 'video' : 'moment'}{result.count !== 1 ? 's' : ''} found
          </span>
          {result.maxSimilarity > 0 && (
            <span className="text-xs text-muted-foreground">
              (relevance: {Math.round(result.maxSimilarity * 100)}%)
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground truncate max-w-[200px]">
          "{query}"
        </span>
      </div>

      <div className="p-3 space-y-2">
        {displayResults.map((item: SearchResult, index: number) => (
          <MomentCard
            key={`${item.postId}-${item.startTime || 0}-${index}`}
            playbackId={item.muxPlaybackId}
            startTime={item.startTime || 0}
            endTime={item.endTime || item.duration || 0}
            title={item.title || item.description?.substring(0, 60)}
            postTitle={item.postTitle}
            videoType={item.isFullVideo ? 'full' : 'moment'}
            onWatch={() => {
              setSelectedMoment({
                playbackId: item.muxPlaybackId,
                postId: item.postId,
                startTime: item.startTime || 0,
                endTime: item.endTime || item.duration || 0,
                // For full videos, use postTitle or title as the display title
                title: item.isFullVideo
                  ? (item.postTitle || item.title || 'Full Video')
                  : item.title,
                momentType: item.isFullVideo ? 'full_video' : (item.momentType || 'moment'),
                postTitle: item.postTitle || item.title,
                similarity: item.similarity,
              });
            }}
          />
        ))}
      </div>

      {hasMore && (
        <div className="px-4 py-2 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? (
              <>
                <ChevronUp className="w-3 h-3 mr-1" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3 mr-1" />
                Show {result.results.length - INITIAL_DISPLAY_COUNT} more
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
